import React, { useState } from 'react';
import { FiUser, FiLock, FiEye, FiEyeOff, FiLogIn, FiArrowLeft } from 'react-icons/fi';
import axios from 'axios';
import InstalarApp from 'shared/components/InstalarApp';
import { getImagenUrl } from 'shared/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function LoginCliente({
  schemaName,
  brandName,
  logoUrl,
  modoRegistro,
  onLoginExitoso,
  onIrARegistro,
  onVolverSimple,
}) {
  const [usuario, setUsuario] = useState('');
  const [clave, setClave] = useState('');
  const [mostrarClave, setMostrarClave] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!usuario.trim() || !clave) {
      setError('Ingresa tu usuario y contraseña');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/auth/login-cliente`, {
        usuario: usuario.trim(),
        clave,
        schema_name: schemaName,
      });
      if (res.data.status === 'success') {
        onLoginExitoso(res.data.data);
      } else {
        setError(res.data.message || 'Error al iniciar sesión');
      }
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.detail || data?.message || 'Error de conexión. Intenta de nuevo.';
      setError(msg);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        <div style={styles.card}>
          <img
            src={
              logoUrl
                ? getImagenUrl(logoUrl)
                : '/logo-agorax.jpeg'
            }
            alt={brandName}
            style={styles.logoImg}
          />
          <h1 style={styles.brand}>{brandName}</h1>
          <p style={styles.subtitle}>Inicia sesión en tu cuenta</p>

          {error && (
            <div style={styles.errorBanner}>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputWrapper}>
              <FiUser size={18} color="var(--color-text-muted)" />
              <input
                type="text"
                placeholder="Usuario"
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                style={styles.input}
                autoComplete="username"
                autoFocus
              />
            </div>

            <div style={styles.inputWrapper}>
              <FiLock size={18} color="var(--color-text-muted)" />
              <input
                type={mostrarClave ? 'text' : 'password'}
                placeholder="Contraseña"
                value={clave}
                onChange={(e) => setClave(e.target.value)}
                style={styles.input}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setMostrarClave(!mostrarClave)}
                style={styles.togglePassword}
                tabIndex={-1}
              >
                {mostrarClave ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>

            <button
              type="submit"
              disabled={enviando}
              style={{
                ...styles.btnPrimary,
                opacity: enviando ? 0.7 : 1,
                cursor: enviando ? 'wait' : 'pointer',
              }}
            >
              {enviando ? (
                <>
                  <span style={styles.spinner} />
                  Iniciando sesión...
                </>
              ) : (
                <>
                  <FiLogIn size={18} />
                  Iniciar sesión
                </>
              )}
            </button>
          </form>

          <div style={styles.links}>
            <button onClick={onIrARegistro} style={styles.linkBtn}>
              Crear cuenta
            </button>
          </div>
        </div>

        <InstalarApp appName={brandName} />

        {modoRegistro === 'simple' && onVolverSimple && (
          <button onClick={onVolverSimple} style={styles.volverBtn}>
            <FiArrowLeft size={16} />
            Continuar sin cuenta
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--color-bg)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    overflow: 'auto',
  },
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 20px',
    minHeight: 0,
  },
  card: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '24px',
    padding: '36px 28px 28px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 4px 32px rgba(11,26,74,0.08)',
    textAlign: 'center',
  },
  logoImg: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    objectFit: 'contain',
    marginBottom: '12px',
  },
  brand: {
    fontSize: '22px',
    fontWeight: 700,
    color: 'var(--color-primary)',
    margin: '0 0 4px',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
    margin: '0 0 24px',
  },
  errorBanner: {
    fontSize: '13px',
    color: 'var(--color-danger)',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    padding: '10px 14px',
    borderRadius: '12px',
    marginBottom: '16px',
    textAlign: 'left',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: 'var(--color-bg)',
    borderRadius: '12px',
    padding: '12px 14px',
    border: '1px solid var(--color-border)',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: '15px',
    fontFamily: 'inherit',
    color: 'var(--color-text)',
  },
  togglePassword: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: 'var(--color-text-muted)',
    display: 'flex',
    alignItems: 'center',
  },
  btnPrimary: {
    padding: '14px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: 'var(--color-secondary)',
    color: 'var(--color-surface)',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '6px',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'opacity 0.2s, transform 0.15s',
  },
  spinner: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'var(--color-surface)',
    animation: 'spin 0.6s linear infinite',
  },
  links: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    fontSize: '13px',
    color: 'var(--color-secondary)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 600,
    padding: '4px 2px',
  },
  linkSep: {
    color: 'var(--color-text-muted)',
    fontSize: '13px',
  },
  volverBtn: {
    marginTop: '16px',
    background: 'none',
    border: '1px solid var(--color-border)',
    borderRadius: '12px',
    padding: '10px 20px',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
};

export default LoginCliente;
